import { useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { shortDate } from '@/lib/format';
import { useDrawings, useReviewDrawing, useSubmitDrawing } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { ShopDrawing, ShopDrawingStatus, Trade } from '@/lib/types';

const TRADES: Trade[] = ['건축', '구조', '기계', '전기', '통신', '토목', '조경', '기타'];

const STATUS: Record<ShopDrawingStatus, { label: string; tone: string }> = {
  SUBMITTED: { label: '검토 대기', tone: 'badge-blue' },
  RESUBMITTED: { label: '재제출', tone: 'badge-blue' },
  APPROVED: { label: '승인', tone: 'badge-gray' },
  REJECTED: { label: '반려', tone: 'badge-red' },
};

/**
 * 시공도·자재 승인.
 *
 * <p>도면등록부(DOC-81 #13)의 축소판이다 — 지금 `DocumentFile` 은 파일 메타만 있고 <b>도면번호
 * 컬럼이 없다.</b> 그래서 「S-201 Rev.B 가 승인됐는지」를 물을 자리가 어디에도 없었다.
 *
 * <p>협력사가 제출하고 감리가 승인·반려한다. <b>반려에는 사유가 필수</b>다 — 검측의 지적사항과
 * 같은 규칙이고, 사유 없는 반려는 협력사가 무엇을 고쳐야 할지 모른 채 다시 올리게 만든다.
 */
export function DrawingsPage() {
  const { siteId, siteName, isJudge, isVendor } = useSite();
  const drawings = useDrawings(siteId);
  const submit = useSubmitDrawing(siteId);
  const review = useReviewDrawing(siteId);

  const [open, setOpen] = useState(false);
  const [drawingNo, setDrawingNo] = useState('');
  const [title, setTitle] = useState('');
  const [revision, setRevision] = useState('A');
  const [kind, setKind] = useState<'시공도' | '자재'>('시공도');
  const [trade, setTrade] = useState<Trade>('건축');

  const [reviewing, setReviewing] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  const rows = drawings.data ?? [];
  // 제출은 협력사만, 검토는 판정하는 손(감리·직원)이다(DOC-87 §2-3).
  const canSubmit = isVendor;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await submit.mutateAsync({ drawingNo: drawingNo.trim(), title: title.trim(), revision: revision.trim(), kind, trade });
    setOpen(false);
    setDrawingNo('');
    setTitle('');
  }

  async function onReview(row: ShopDrawing, status: 'APPROVED' | 'REJECTED') {
    if (status === 'REJECTED' && comment.trim() === '') return;
    await review.mutateAsync({ id: row.id, status, comment: comment.trim() });
    setReviewing(null);
    setComment('');
  }

  return (
    <>
      <PageHeader
        title="시공도·자재 승인"
        subtitle={siteName}
        action={
          canSubmit && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              제출
            </button>
          )
        }
      />

      {open && (
        <form onSubmit={onSubmit} className="card mb-3">
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>도면번호</span>
              <input type="text" className="input-field" value={drawingNo} onChange={(e) => setDrawingNo(e.target.value)} placeholder="S-201" required />
            </label>
            <label className="w-24">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>Rev</span>
              <input type="text" className="input-field" value={revision} onChange={(e) => setRevision(e.target.value)} required />
            </label>
          </div>
          <label className="mb-3 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>제목</span>
            <input type="text" className="input-field" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </label>
          <div className="mb-4 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>구분</span>
              <select className="input-field" value={kind} onChange={(e) => setKind(e.target.value as '시공도' | '자재')}>
                <option value="시공도">시공도</option>
                <option value="자재">자재</option>
              </select>
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>공종</span>
              <select className="input-field" value={trade} onChange={(e) => setTrade(e.target.value as Trade)}>
                {TRADES.map((it) => <option key={it} value={it}>{it}</option>)}
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={submit.isPending}>
              {submit.isPending ? '보내는 중입니다…' : '제출'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={drawings.isPending}
        error={drawings.isError}
        empty={rows.length === 0}
        emptyText="제출된 도면이 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card">
              <div className="mb-1 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-ui-md font-medium">
                    {row.drawingNo} <span className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>Rev.{row.revision}</span>
                  </div>
                  <div className="truncate text-ui-sm">{row.title}</div>
                  <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    {row.kind} · {row.trade ?? '공종 미지정'} · {shortDate(row.submittedOn)} 제출
                  </div>
                </div>
                <span className={`badge shrink-0 ${STATUS[row.status].tone}`}>{STATUS[row.status].label}</span>
              </div>

              {row.comment !== null && (
                <p className="mt-2 rounded-md p-2 text-ui-sm" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>
                  {row.comment}
                </p>
              )}

              {isJudge && (row.status === 'SUBMITTED' || row.status === 'RESUBMITTED') && (
                reviewing === row.id ? (
                  <div className="mt-3">
                    <textarea
                      className="input-field mb-2"
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="반려 사유"
                    />
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary flex-1" onClick={() => onReview(row, 'APPROVED')} disabled={review.isPending}>
                        승인
                      </button>
                      <button
                        type="button"
                        className="btn-secondary flex-1"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => onReview(row, 'REJECTED')}
                        disabled={review.isPending || comment.trim() === ''}
                      >
                        반려
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="btn-secondary mt-3 w-full" onClick={() => { setReviewing(row.id); setComment(''); }}>
                    검토
                  </button>
                )
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
